<script setup lang="ts">
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import { SimpleDialog, SimpleDialogContent, SimpleDialogDescription, SimpleDialogFooter, SimpleDialogHeader, SimpleDialogTitle } from "ls/components/simple-dialog";
import { toOrganizationMembers } from "ls/router/builders";
import { useSubInstanceId } from "ls/state/useSubInstance";
import { DialogClose } from "reka-ui";
import { computed } from "vue";
import { RpErrorTypes } from "@/generated/models";
import { useOrganization } from "../organizations/useOrganization";
import { useAccessLevelFromOrganization } from "../organizations/useOrganizationAccessLevel";
import { useAxureRpRoleFromOrganization } from "../organizations/useOrganizationAxureRpRole";
import { useAccessControlErrorsFilter } from "./useAccessControl";

const subInstanceId = useSubInstanceId();
const organization = useOrganization(subInstanceId);
const { isAdmin } = useAccessLevelFromOrganization(organization);
const { isPublisher } = useAxureRpRoleFromOrganization(organization);

const { isOpen, error, isError } = useAccessControlErrorsFilter([
  RpErrorTypes.SubInstanceLicenseAuditFailed,
  RpErrorTypes.NoPermissionToAccess,
  RpErrorTypes.ClickthroughPublishRequiresLicense,
]);

const isExpoProject = computed(() => error.value?.type === RpErrorTypes.ClickthroughPublishRequiresLicense);
</script>

<template>
  <SimpleDialog
    v-if="isError"
    v-model:open="isOpen"
  >
    <SimpleDialogContent>
      <SimpleDialogHeader>
        <SimpleDialogTitle>
          <template v-if="isPublisher">
            Assign an Axure RP Publisher seat
          </template>
          <template v-else>
            Change your Axure RP role to publisher
          </template>
        </SimpleDialogTitle>
        <SimpleDialogDescription>
          <template v-if="isAdmin">
            <template v-if="isPublisher">
              If you have Axure RP Enterprise, publish from Axure RP to verify your license. If not, assign an Axure RP publisher seat to your account on the members page.
            </template>
            <template v-else-if="isExpoProject">
              To create or edit clickthrough projects, change your Axure RP role to <span class="font-semibold">publisher</span> on the members page
            </template>
            <template v-else>
              To publish Axure RP files, change your Axure RP role to <span class="font-semibold">publisher</span> on the members page
            </template>
          </template>
          <template v-else>
            <template v-if="isPublisher">
              If you have Axure RP Enterprise, publish from Axure RP to verify your license. If not, ask an admin to assign an Axure RP publisher seat to your account on the members page.
            </template>
            <template v-else-if="isExpoProject">
              To create or edit clickthrough projects, ask an admin to change your Axure RP role to <span class="font-semibold">publisher</span> on the members page
            </template>
            <template v-else>
              To publish Axure RP files, ask an admin to change your Axure RP role to <span class="font-semibold">publisher</span> on the members page
            </template>
          </template>
        </SimpleDialogDescription>
      </SimpleDialogHeader>
      <SimpleDialogFooter>
        <DialogClose>
          <SimpleButton
            v-if="isAdmin && organization"
            :href="toOrganizationMembers(organization)"
            size="large"
          >
            Go to members
          </SimpleButton>
          <SimpleButton
            v-else
            size="large"
          >
            Got it
          </SimpleButton>
        </DialogClose>
      </SimpleDialogFooter>
    </SimpleDialogContent>
  </SimpleDialog>
</template>
